<template>
  <div class="technician-board-container">
    <div class="header-container">
      <div class="header">
        TECHNICIANS
      </div>
      <div class="filter-container">
        <SACDropdown
          hideSearch
          field="selectedTimespan"
          :options="timespanOptions"
          :value="selectedTimespan"
          :change="handleChange"
        />
        <SACDropdown
          hideSearch
          field="selectedSort"
          :options="sortOptions"
          :value="selectedSort"
          :change="handleChange"
        />
      </div>
    </div>
    <div class="tech-body-container">
      <div
        v-if="loading || techsLoading"
        class="hero-number"
      >
        <img
          src="@shared/assets/img/general/loading-white.svg"
          alt="Loading"
        >
      </div>
      <div
        v-else-if="!techsLoading && boardData.length === 0"
        class="empty-text"
      >
        No Technicians
      </div>
      <BoardItem
        v-for="(technician, idx) in boardData"
        v-else
        :key="idx"
        :header="technician.techName"
        :number="technician.count"
        :change="technician.change"
        :link="`/adoption/installations?technician=${technician.technicianId}`"
      />
    </div>
    <div
      v-if="!techsLoading && technicianData.length > 10"
      class="footer-container"
    >
      <a
        v-if="showSliced"
        class="all-link"
        @click="showSliced = false"
      >
        See All
      </a>
      <a
        v-if="!showSliced"
        class="all-link"
        @click="showSliced = true"
      >
        Collapse All
      </a>
    </div>
  </div>
</template>

<script>
import adoptionMixin from '@shared/mixins/adoptionMixin';

import SACDropdown from '@shared/components/sac/SACDropdown.vue';
import BoardItem from './board/BoardItem.vue';

export default {
  name: 'TechnicianBoard',
  components: {
    SACDropdown,
    BoardItem,
  },
  mixins: [adoptionMixin],
  data() {
    return {
      selectedTimespan: '30day',
      selectedSort: 'total',
      showSliced: true,
      loading: false,
      noTechList: false,
      timespanOptions: [{
        label: 'Last 7 Days',
        value: '7day',
      }, {
        label: 'Last 30 Days',
        value: '30day',
      }, {
        label: 'Last 60 Days',
        value: '60day',
      }, {
        label: 'Last 6 Months',
        value: '6month',
      }, {
        label: 'Last 12 Months',
        value: '12month',
      }, {
        label: 'All Time',
        value: 'allTime',
      }],
      sortOptions: [{
        label: '0 Units Installed',
        value: '0unit',
      }, {
        label: '1 Unit Installed',
        value: '1unit',
      }, {
        label: 'Total Units Installed',
        value: 'total',
      }, {
        label: 'Most Units Installed',
        value: 'most',
      }, {
        label: 'Least Units Installed',
        value: 'least',
      }, {
        label: 'Alphabetical',
        value: 'alphabetical',
      }],
    };
  },
  computed: {
    techsLoading() {
      return this.loadingTechs;
    },
    boardData() {
      const {
        showSliced,
      } = this;
      if (!this.technicianData) {
        return [];
      }
      if (showSliced) {
        return this.technicianData.slice(0, 10);
      }
      return this.technicianData;
    },
    technicianData() {
      const {
        selectedTimespan,
        selectedSort,
      } = this;
      if (!this.technicians) {
        return [];
      }
      let newTechnicians = this.technicians.map((overview) => {
        const {
          technicianId,
          techName,
          allTime,
          lastSevenDays,
          prevSevenDays,
          lastThirtyDays,
          prevThirtyDays,
          lastSixtyDays,
          prevSixtyDays,
          lastSixMonths,
          prevSixMonths,
          lastTwelveMonths,
          prevTwelveMonths,
        } = overview;
        let change = 'none';
        let count = 0;
        if (selectedTimespan === '7day') {
          if (lastSevenDays < prevSevenDays) {
            change = 'down';
          } else if (lastSevenDays > prevSevenDays) {
            change = 'up';
          }
          count = lastSevenDays;
        }
        if (selectedTimespan === '30day') {
          if (lastThirtyDays < prevThirtyDays) {
            change = 'down';
          } else if (lastThirtyDays > prevThirtyDays) {
            change = 'up';
          }
          count = lastThirtyDays;
        }
        if (selectedTimespan === '60day') {
          if (lastSixtyDays < prevSixtyDays) {
            change = 'down';
          } else if (lastSixtyDays > prevSixtyDays) {
            change = 'up';
          }
          count = lastSixtyDays;
        }
        if (selectedTimespan === '6month') {
          if (lastSixMonths < prevSixMonths) {
            change = 'down';
          } else if (lastSixMonths > prevSixMonths) {
            change = 'up';
          }
          count = lastSixMonths;
        }
        if (selectedTimespan === '12month') {
          if (lastTwelveMonths < prevTwelveMonths) {
            change = 'down';
          } else if (lastTwelveMonths > prevTwelveMonths) {
            change = 'up';
          }
          count = lastTwelveMonths;
        }
        if (selectedTimespan === 'allTime') {
          change = '';
          count = allTime;
        }
        return {
          technicianId,
          techName,
          count,
          change,
        };
      });
      if (selectedSort === '0unit' || selectedSort === '1unit') {
        newTechnicians = newTechnicians.filter((tech) => {
          if (selectedSort === '0unit') {
            return tech.count === 0;
          }
          if (selectedSort === '1unit') {
            return tech.count === 1;
          }
          return false;
        });
      }

      return newTechnicians.sort((a, b) => {
        if (selectedSort === 'total') {
          return b.count - a.count;
        }
        if (selectedSort === 'most') {
          return b.count - a.count;
        }
        if (selectedSort === 'least') {
          return a.count - b.count;
        }
        if (selectedSort === 'alphabetical'
          || selectedSort === '0unit'
          || selectedSort === '1unit') {
          if (a.techName.toLowerCase() > b.techName.toLowerCase()) {
            return 1;
          }
          if (a.techName.toLowerCase() < b.techName.toLowerCase()) {
            return -1;
          }
        }
        return 0;
      });
    },
  },
  methods: {
    handleChange(field, value) {
      this[field] = value;
    },
  },
};
</script>

<style lang="scss">

.technician-board-container {
  flex: 1;
  background-color: $N95;
  padding: 16px 24px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  margin-right: 34px;
  max-height: 800px;
  min-height: 300px;
  overflow: auto;
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;

  /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }

  .header-container {
    display: grid;
    gap: 10px;
    grid-template-columns: auto 1fr;
    align-items: center;
    margin-bottom: 16px;

    .header {
      font-size: 0.875rem;
      color: $WHITE;
      font-weight: 700;
    }

    .filter-container {
      display: flex;

      #sac-dropdown-container {
        min-width: auto;

        .sac-dropdown {
          background-color: transparent;
          justify-content: space-evenly;

          .sac-value {
            color: $WHITE;
            font-size: 0.875rem;
            font-weight: 700;
            text-decoration: underline;
            text-transform: uppercase;
          }

          svg {
            path {
              fill: $WHITE;
            }
          }
        }
      }

      .filter-button {
        font-size: 0.875rem;
        color: $WHITE;
        font-weight: 700;
        display: flex;
        align-items: flex-end;
        text-decoration: underline;

        &:first-child {
          margin-right: 12px;
        }

        .button-icon {
          width: 20px;
          object-fit: contain;
        }
      }
    }
  }

  .tech-body-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-height: 900px;
    overflow: auto;

    .empty-text {
      color: $WHITE;
      font-weight: 500;
    }
  }

  .footer-container {
    margin-top: 14px;

    .all-link {
      color: $N60;
      text-decoration: underline;
      font-weight: 500;
    }
  }
}
</style>
